import BigNumber from 'bignumber.js'
import { BUYBACK_FEE } from 'config/constants/info'
import { XVVS_LAUNCH_DATE } from 'config/constants/xvvs'

import { getUnixTime, add, isBefore, addDays } from 'date-fns'
import sum from 'lodash/sum'
import { useMemo } from 'react'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { useFetchVVSBarHistory } from 'state/info/queries/bar/barHistory'
import { useFetchVvsDayData } from 'state/info/queries/protocol/chart'
import { BarHistoryData } from 'state/info/queries/types'
import { ChartEntry } from 'state/info/types'
import useXvvsRate from 'views/xVvs/hooks/useXvvsRate'
import { Price } from 'vvs-sdk'

type Props =
  | {
      timeOffset: Duration
    }
  | {
      since: number // Unix Timestamp
    }

const FIXED_SUBSIDY = 600_000_000
const CAMPAIGN_START_DATETIME = new Date('2022-10-13T08:00:00Z')
const DURATION = 60

export const calculateDayApr = ({
  barData,
  vvsDayData,
  vvsPrice,
  rate,
}: {
  barData: BarHistoryData
  vvsDayData: ChartEntry
  vvsPrice: BigNumber
  rate: Price
}) => {
  if (rate.equalTo('1')) {
    return 0
  }
  const currentXvvsPrice = new BigNumber(barData.ratio).times(vvsPrice).toNumber()
  const currentXvvsSupply = new BigNumber(barData.totalSupply).toNumber()
  const apr =
    (((vvsDayData.volumeUSD * BUYBACK_FEE +
      (isBefore(new Date(), addDays(CAMPAIGN_START_DATETIME, DURATION)) ? FIXED_SUBSIDY * vvsPrice.toNumber() : 0)) /
      currentXvvsSupply) *
      365) /
    currentXvvsPrice
  return apr * 100 // percentage
}

export const useXvvsApr = (options: Props) => {
  const vvsPrice = usePriceVvsUsdc()
  const coeff = 1000 * 60
  const timestamp = new Date()
  const roundedTimestamp = new Date(Math.floor(timestamp.getTime() / coeff) * coeff)
  const timeQuery = 'since' in options ? options.since : getUnixTime(add(roundedTimestamp, options.timeOffset))
  const { data: vvsDayData } = useFetchVvsDayData(timeQuery)
  const { data: vvsBarHistory, error } = useFetchVVSBarHistory(timeQuery)
  const rate = useXvvsRate()

  const { aprs }: { aprs: { date: number; value: number }[] } = useMemo(() => {
    return !error && vvsBarHistory?.barDailySnapshots && vvsDayData
      ? vvsBarHistory.barDailySnapshots.reduce(
          (previousValue, currentBarData) => {
            const date = parseInt(currentBarData.date) * 1000
            const dayData = vvsDayData.find((d) => d.date === parseInt(currentBarData.date))
            // exclude launch date data
            if (!dayData || date <= XVVS_LAUNCH_DATE) {
              return previousValue
            }
            return {
              ...previousValue,
              aprs: [
                ...previousValue.aprs,
                {
                  date,
                  value: calculateDayApr({
                    barData: currentBarData,
                    vvsDayData: dayData,
                    vvsPrice,
                    rate,
                  }),
                },
              ],
            }
          },
          {
            aprs: [],
          },
        )
      : { aprs: [] }
  }, [error, rate, vvsBarHistory, vvsDayData, vvsPrice])
  if (rate.equalTo('1')) {
    return {
      dailyAprs: [],
      avgApr: 0,
    }
  }
  const validAprs = aprs.filter((x) => x.value !== Infinity).map((v) => v.value)
  const totalPeriodApr = sum(validAprs)
  return {
    dailyAprs: aprs,
    avgApr: totalPeriodApr / validAprs.length,
  }
}
